import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { Reveal, Chapter } from "./Reveal";
import { useLang } from "@/lib/i18n";

const STAGE_STYLE = {
  "PHASE 1": "text-emerald-400 border-emerald-500/40",
  "IN DEVELOPMENT": "text-gold border-gold/40",
  PILOT: "text-emerald-300 border-emerald-400/40",
  ACTIVE: "text-emerald-300 border-emerald-400/40",
  CONCEPT: "text-white/50 border-white/20",
  FUTURE: "text-white/40 border-white/15",
};

export default function Projects() {
  const { t, lang } = useLang();
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  const lp = (p, f) => (lang === "pt" && p[`${f}_pt`]) ? p[`${f}_pt`] : p[f];
  const words = t("projects.chapter").split(" ");
  const last = words.pop();

  return (
    <section id="projects" data-testid="projects-section" className="py-28 lg:py-40 bg-surface/40 border-y border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <Chapter index="13" label={t("projects.chapter")} testid="projects-chapter" />
        <Reveal>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-ivory leading-[1.08]">
            {words.join(" ")} <span className="italic text-gold-light">{last}</span>
          </h2>
        </Reveal>

        <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {projects.map((p, i) => (
            <motion.article
              key={p.id}
              data-testid={`project-card-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
              className="group bg-ink flex flex-col hover:bg-surface transition-colors duration-500"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: (i % 3) * 0.1 }}
            >
              <div className="relative h-48 overflow-hidden border-b border-white/10">
                {p.image_url ? (
                  <img src={fileUrl(p.image_url)} alt={p.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                ) : (
                  <div className="w-full h-full bg-[#0B1526] flex items-center justify-center">
                    <span className="font-serif text-5xl text-gold/20">{p.name.charAt(0)}</span>
                  </div>
                )}
                <div className="absolute inset-0 bg-ink/45" />
                <span className={`absolute top-4 left-4 border px-3 py-1 font-mono text-[9px] tracking-[0.25em] uppercase bg-ink/70 backdrop-blur-sm ${STAGE_STYLE[p.stage] || STAGE_STYLE.CONCEPT}`}>
                  {p.stage}
                </span>
              </div>
              <div className="p-8 flex flex-col flex-1">
                <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-gold">{lp(p, "category")}</span>
                <h3 className="font-serif text-2xl text-ivory mt-3">{p.name}</h3>
                <p className="mt-1 font-mono text-[10px] tracking-[0.2em] uppercase text-white/35">{lp(p, "market")}</p>
                <p className="mt-4 text-sm text-white/55 leading-relaxed flex-1">{lp(p, "description")}</p>
                <div className="mt-6 flex items-center gap-5">
                  {p.website && (
                    <a data-testid={`project-site-${p.name.toLowerCase().replace(/\s+/g, "-")}`} href={p.website} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase text-gold hover:text-gold-light transition-colors">
                      {t("projects.site")} <ArrowUpRight size={12} />
                    </a>
                  )}
                  {lp(p, "details") && (
                    <details className="group/d">
                      <summary data-testid={`project-details-${p.name.toLowerCase().replace(/\s+/g, "-")}`} className="cursor-pointer list-none font-mono text-[10px] tracking-[0.2em] uppercase text-white/50 hover:text-ivory transition-colors">
                        {t("projects.details")}
                      </summary>
                      <p className="mt-3 text-sm text-white/50 leading-relaxed">{lp(p, "details")}</p>
                    </details>
                  )}
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
